import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "context/AuthContext";
import { apiGet, apiPut, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import dayjs from "dayjs";
import LocationMap from "components/GoogleMap/LocationMap";
import ConfirmationModal from "components/reusable/ConfirmationModal";

// GEO ACCESS: Location column = GPS loginAddress only.
// IP city / region / country is commented — uncomment fromIp to show it again.
const formatGeoLogLocation = (log) => {
  const gps = String(log?.loginAddress || "").trim();
  if (gps) return gps;
  // const fromIp = [log?.city, log?.regionName, log?.countryName]
  //   .filter(Boolean)
  //   .join(", ");
  // return fromIp || "-";
  return "-";
};

const DIAL_TO_ISO = {
  91: "IN",
  1: "US",
  44: "GB",
  61: "AU",
  971: "AE",
  92: "PK",
  880: "BD",
  94: "LK",
  65: "SG",
  60: "MY",
  63: "PH",
  62: "ID",
  66: "TH",
  81: "JP",
  82: "KR",
  86: "CN",
  49: "DE",
  33: "FR",
  39: "IT",
  34: "ES",
  55: "BR",
  52: "MX",
  27: "ZA",
  234: "NG",
  254: "KE",
  20: "EG",
  966: "SA",
  974: "QA",
  64: "NZ",
};

const NAME_TO_ISO = {
  INDIA: "IN",
  "UNITED STATES": "US",
  USA: "US",
  US: "US",
  AMERICA: "US",
  CANADA: "CA",
  "UNITED KINGDOM": "GB",
  UK: "GB",
  AUSTRALIA: "AU",
  "UNITED ARAB EMIRATES": "AE",
  UAE: "AE",
  PAKISTAN: "PK",
  BANGLADESH: "BD",
  NIGERIA: "NG",
  KENYA: "KE",
  EGYPT: "EG",
  GERMANY: "DE",
  FRANCE: "FR",
  CHINA: "CN",
  JAPAN: "JP",
  SINGAPORE: "SG",
  MALAYSIA: "MY",
  INDONESIA: "ID",
  PHILIPPINES: "PH",
  BRAZIL: "BR",
  MEXICO: "MX",
  "SOUTH AFRICA": "ZA",
  "SAUDI ARABIA": "SA",
  QATAR: "QA",
  "SRI LANKA": "LK",
};

function toIsoCountry(country) {
  const name = String(country?.name || "")
    .trim()
    .toUpperCase();
  if (NAME_TO_ISO[name]) return NAME_TO_ISO[name];
  if (name.includes("UNITED STATES") || name === "USA") return "US";
  if (name.includes("CANADA")) return "CA";
  if (name.includes("NIGERIA")) return "NG";
  if (name.includes("INDIA")) return "IN";
  const code = String(country?.countryCode || country?.code || "")
    .toUpperCase()
    .replace(/^\+/, "");
  if (code.length === 2 && /^[A-Z]{2}$/.test(code) && !NAME_TO_ISO[name]) {
    // Keep 2-letter ISO only when the name did not already pick a country.
    if (code !== "IN" || name.includes("INDIA")) return code;
  }
  if (code === "1") return name.includes("CANADA") ? "CA" : "US";
  return DIAL_TO_ISO[code] || DIAL_TO_ISO[Number(code)] || code;
}

function normalizeCountryRows(rows) {
  return (rows || []).map((row) => ({
    ...row,
    code: toIsoCountry(row),
  }));
}

const GeoAccess = () => {
  const { t } = useTranslation();
  const { updatePageName } = useContext(AuthContext);
  const notification = useToastContext();
  const [tab, setTab] = useState("countries");
  const [policy, setPolicy] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [countryPick, setCountryPick] = useState("");
  const [countryPicks, setCountryPicks] = useState([]);
  const [statePick, setStatePick] = useState("");
  const [statePicks, setStatePicks] = useState([]);
  // GEO ACCESS: IP lists moved to company admin Edit profile → Geographic Access.
  // const [ipValue, setIpValue] = useState("");
  // const [ipLabel, setIpLabel] = useState("");
  // const [ipListType, setIpListType] = useState("allow");
  const [logs, setLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [saving, setSaving] = useState(false);
  const [ipValue, setIpValue] = useState("");
  const [ipLabel, setIpLabel] = useState("");
  const [employees, setEmployees] = useState([]);
  const [empPage, setEmpPage] = useState(1);
  const [empTotalPages, setEmpTotalPages] = useState(1);
  const [empSearch, setEmpSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [listConfirm, setListConfirm] = useState(null);

  const loadPolicy = async () => {
    try {
      const res = await apiGet(apiPath.geoAccessPolicy);
      if (res?.data?.success) {
        const next = res.data.results || {};
        setPolicy({
          ...next,
          allowedCountries: normalizeCountryRows(next.allowedCountries),
          blockedCountries: normalizeCountryRows(next.blockedCountries),
          highRiskCountries: normalizeCountryRows(next.highRiskCountries),
        });
      } else notification.error(res?.data?.message || "Unable to load geo policy");
    } catch (err) {
      notification.error(
        err?.response?.data?.message ||
        "Geographic Access API is not running. Restart empivo-api on port 2011.",
      );
    }
  };

  const loadCountries = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {});
      const list = res?.data?.results?.allCountries || [];
      setCountries(list.filter((c) => String(c.status || "active").toLowerCase() === "active"));
    } catch (err) {
      console.log("Error failed to load geo countries", err?.message);
    }
  };

  const loadLogs = async (page = 1) => {
    try {
      const res = await apiGet(apiPath.geoAccessLogs, { page, pageSize: 10 });
      if (res?.data?.success) {
        setLogs(res.data.results?.docs || []);
        setLogPage(res.data.results?.page || 1);
        setLogTotalPages(res.data.results?.totalPages || 1);
      }
    } catch (err) {
      notification.error(err?.response?.data?.message || "Unable to load geo logs");
    }
  };

  const deleteLog = async (id) => {
    const logId = String(id || "");
    if (!logId || logId === "undefined") return;
    if (!window.confirm("Delete this geo log?")) return;
    try {
      const res = await apiDelete(`${apiPath.geoAccessLogs}/${logId}`);
      if (res?.data?.success) {
        notification.success(res.data.message || "Log deleted");
        const nextPage = logs.length === 1 && logPage > 1 ? logPage - 1 : logPage;
        loadLogs(nextPage);
      } else {
        notification.error(res?.data?.message || "Unable to delete geo log");
      }
    } catch (err) {
      notification.error(err?.response?.data?.message || "Unable to delete geo log");
    }
  };

  useEffect(() => {
    updatePageName(t("GEO_ACCESS"));
    loadPolicy();
    loadCountries();
  }, []);

  const loadEmployeeLocations = async (page = 1, search = empSearch) => {
    try {
      const res = await apiGet(apiPath.geoAccessEmployeeLocations, {
        page,
        pageSize: 10,
        search,
      });
      if (res?.data?.success) {
        const docs = res.data.results?.docs || [];
        setEmployees(docs);
        setEmpPage(res.data.results?.page || 1);
        setEmpTotalPages(res.data.results?.totalPages || 1);
        setSelectedEmployee((prev) => {
          if (prev && docs.some((d) => String(d._id) === String(prev._id))) return prev;
          return docs[0] || null;
        });
      }
    } catch (err) {
      notification.error(
        err?.response?.data?.message ||
        "Unable to load employee locations. Restart empivo-api on port 2011.",
      );
    }
  };

  useEffect(() => {
    const loadStates = async () => {
      if (!selectedCountryId) {
        setStates([]);
        return;
      }
      const res = await apiGet(`${apiPath.getStateListByCountry}/${selectedCountryId}`);
      setStates(res?.data?.results?.allStates || []);
    };
    loadStates();
  }, [selectedCountryId]);

  useEffect(() => {
    if (tab === "logs") loadLogs(1);
    if (tab === "employees") loadEmployeeLocations(1);
  }, [tab]);

  const savePolicy = async (next) => {
    try {
      setSaving(true);
      const res = await apiPut(apiPath.geoAccessPolicy, next);
      if (res?.data?.success) {
        const next = res.data.results || {};
        setPolicy({
          ...next,
          allowedCountries: normalizeCountryRows(next.allowedCountries),
          blockedCountries: normalizeCountryRows(next.blockedCountries),
          highRiskCountries: normalizeCountryRows(next.highRiskCountries),
        });
        notification.success(res.data.message);
      } else {
        notification.error(res?.data?.message || "Save failed");
      }
    } catch (err) {
      notification.error(err.message);
    }
    setSaving(false);
  };

  const patch = (partial) => {
    const next = { ...policy, ...partial };
    setPolicy(next);
    return next;
  };

  const COUNTRY_LIST_LABEL = {
    allowedCountries: "Allowed",
    blockedCountries: "Blocked",
    highRiskCountries: "High-risk",
  };
  const STATE_LIST_LABEL = {
    allowedStates: "Allowed",
    blockedStates: "Blocked",
  };

  const sameCountryRow = (entry, row) =>
    String(row.countryId || "") === String(entry.countryId || "") ||
    String(row.code || "").toUpperCase() === String(entry.code || "").toUpperCase() ||
    String(row.name || "").toLowerCase() === String(entry.name || "").toLowerCase();

  const sameStateRow = (entry, row) =>
    (entry.stateId && String(row.stateId || "") === String(entry.stateId)) ||
    (String(row.stateCode || "").toUpperCase() === String(entry.stateCode || "").toUpperCase() &&
      (String(row.countryCode || "").toUpperCase() === String(entry.countryCode || "").toUpperCase() ||
        String(row.countryName || "").toLowerCase() === String(entry.countryName || "").toLowerCase())) ||
    (String(row.stateName || "").toLowerCase() === String(entry.stateName || "").toLowerCase() &&
      (String(row.countryCode || "").toUpperCase() === String(entry.countryCode || "").toUpperCase() ||
        String(row.countryName || "").toLowerCase() === String(entry.countryName || "").toLowerCase()));

  const askRemove = (name, run, fromList) => {
    setListConfirm({
      title: "Remove?",
      message: `Do you still want to remove ${name}${fromList ? ` from ${fromList}` : ""}?`,
      confirmText: "Yes, remove",
      run,
    });
  };

  const askChangeList = (message, run) => {
    setListConfirm({
      title: "Change list?",
      message,
      confirmText: "Yes, change",
      run,
    });
  };

  const addCountry = (listKey) => {
    addCountries(listKey, countryPicks.length ? countryPicks : countryPick ? [countryPick] : []);
  };

  const addCountries = (listKey, ids, options = {}) => {
    const selectedIds = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (!selectedIds.length) {
      notification.error("Select at least one country");
      return;
    }
    const lists = {
      allowedCountries: [...(policy.allowedCountries || [])],
      blockedCountries: [...(policy.blockedCountries || [])],
      highRiskCountries: [...(policy.highRiskCountries || [])],
    };
    const others = {
      allowedCountries: ["blockedCountries", "highRiskCountries"],
      blockedCountries: ["allowedCountries", "highRiskCountries"],
      highRiskCountries: ["allowedCountries", "blockedCountries"],
    };
    const targetKey = listKey;
    const target = lists[targetKey];
    if (!target) return;
    const moves = [];
    selectedIds.forEach((id) => {
      const country = countries.find((c) => String(c._id) === String(id));
      if (!country) return;
      const entry = {
        code: toIsoCountry(country),
        name: country.name,
        countryId: country._id,
      };
      Object.keys(COUNTRY_LIST_LABEL).forEach((key) => {
        if (key === targetKey) return;
        if ((policy[key] || []).some((row) => sameCountryRow(entry, row))) {
          moves.push({
            name: country.name,
            from: COUNTRY_LIST_LABEL[key],
            to: COUNTRY_LIST_LABEL[targetKey],
          });
        }
      });
    });
    if (moves.length && !options.confirmed) {
      const details = moves
        .map((m) => `${m.name} from ${m.from} to ${m.to}`)
        .join(", ");
      askChangeList(`Do you still want to change ${details}?`, () =>
        addCountries(listKey, ids, { confirmed: true }),
      );
      return;
    }
    let added = 0;
    let moved = 0;
    selectedIds.forEach((id) => {
      const country = countries.find((c) => String(c._id) === String(id));
      if (!country) return;
      const isoCode = toIsoCountry(country);
      const entry = {
        code: isoCode,
        name: country.name,
        countryId: country._id,
      };
      if (!target.some((row) => sameCountryRow(entry, row))) {
        target.push(entry);
        added += 1;
      }
      (others[targetKey] || []).forEach((otherKey) => {
        const before = lists[otherKey].length;
        lists[otherKey] = lists[otherKey].filter((row) => !sameCountryRow(entry, row));
        if (lists[otherKey].length !== before) moved += 1;
      });
    });
    if (!added && !moved) {
      notification.error("Country already added");
      return;
    }
    const next = {
      allowedCountries: lists.allowedCountries,
      blockedCountries: lists.blockedCountries,
      highRiskCountries: lists.highRiskCountries,
    };
    if (listKey === "allowedCountries" && policy.countryMode === "disabled") {
      next.countryMode = "allow";
    }
    savePolicy(patch(next));
    setCountryPick("");
    setCountryPicks([]);
  };

  const removeCountry = (listKey, code) => {
    savePolicy(
      patch({ [listKey]: (policy[listKey] || []).filter((c) => c.code !== code) }),
    );
    setListConfirm(null);
  };
  const askRemoveCountry = (listKey, row) => {
    askRemove(
      row.name || row.code,
      () => removeCountry(listKey, row.code),
      COUNTRY_LIST_LABEL[listKey],
    );
  };

  const addState = (listKey) => {
    addStates(listKey, statePicks.length ? statePicks : statePick ? [statePick] : []);
  };

  const addStates = (listKey, ids, options = {}) => {
    const country = countries.find((c) => String(c._id) === String(selectedCountryId));
    const selectedIds = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (!country) {
      notification.error("Select a country first");
      return;
    }
    if (!selectedIds.length) {
      notification.error("Select at least one state");
      return;
    }
    const lists = {
      allowedStates: [...(policy.allowedStates || [])],
      blockedStates: [...(policy.blockedStates || [])],
    };
    const others = {
      allowedStates: ["blockedStates"],
      blockedStates: ["allowedStates"],
    };
    const target = lists[listKey];
    if (!target) return;
    const moves = [];
    selectedIds.forEach((id) => {
      const state = (Array.isArray(states) ? states : []).find(
        (s) => String(s._id) === String(id),
      );
      if (!state) return;
      const entry = {
        countryCode: toIsoCountry(country),
        countryName: country.name,
        stateCode: state.code || state.abbreviation || state.name,
        stateName: state.name,
        stateId: state._id,
      };
      Object.keys(STATE_LIST_LABEL).forEach((key) => {
        if (key === listKey) return;
        if ((policy[key] || []).some((row) => sameStateRow(entry, row))) {
          moves.push({
            name: state.name,
            from: STATE_LIST_LABEL[key],
            to: STATE_LIST_LABEL[listKey],
          });
        }
      });
    });
    if (moves.length && !options.confirmed) {
      const details = moves
        .map((m) => `${m.name} from ${m.from} to ${m.to}`)
        .join(", ");
      askChangeList(`Do you still want to change ${details}?`, () =>
        addStates(listKey, ids, { confirmed: true }),
      );
      return;
    }
    let added = 0;
    let moved = 0;
    selectedIds.forEach((id) => {
      const state = (Array.isArray(states) ? states : []).find(
        (s) => String(s._id) === String(id),
      );
      if (!state) return;
      const entry = {
        countryCode: toIsoCountry(country),
        countryName: country.name,
        stateCode: state.code || state.abbreviation || state.name,
        stateName: state.name,
        stateId: state._id,
      };
      if (!target.some((row) => sameStateRow(entry, row))) {
        target.push(entry);
        added += 1;
      }
      (others[listKey] || []).forEach((otherKey) => {
        const before = lists[otherKey].length;
        lists[otherKey] = lists[otherKey].filter((row) => !sameStateRow(entry, row));
        if (lists[otherKey].length !== before) moved += 1;
      });
    });
    if (!added && !moved) {
      notification.error("State already added");
      return;
    }
    const next = {
      allowedStates: lists.allowedStates,
      blockedStates: lists.blockedStates,
    };
    if (listKey === "allowedStates" && policy.stateMode === "disabled") {
      next.stateMode = "allow";
    }
    savePolicy(patch(next));
    setStatePick("");
    setStatePicks([]);
  };

  const companyAdminIps = (policy?.ipAllowlist || []).filter(
    (i) => (i.role || "any") === "companyAdmin" || (i.role || "any") === "any",
  );

  const addCompanyAdminIp = (value, label) => {
    const ip = (value || "").trim();
    if (!ip) return;
    const list = policy.ipAllowlist || [];
    if (list.some((i) => i.value === ip && ((i.role || "any") === "companyAdmin" || (i.role || "any") === "any"))) {
      notification.error("IP already added");
      return;
    }
    savePolicy(
      patch({
        ipAllowlist: [...list, { value: ip, label: (label || "").trim(), role: "companyAdmin" }],
        ipBlocklist: (policy.ipBlocklist || []).filter((i) => i.value !== ip),
      }),
    );
    setIpValue("");
    setIpLabel("");
  };

  const removeCompanyAdminIp = (value) => {
    savePolicy(
      patch({
        ipAllowlist: (policy.ipAllowlist || []).filter(
          (i) => !(i.value === value && ((i.role || "any") === "companyAdmin" || (i.role || "any") === "any")),
        ),
      }),
    );
  };

  const addBlockedIp = (value, label) => {
    const ip = (value || "").trim();
    if (!ip) return;
    const list = policy.ipBlocklist || [];
    if (list.some((i) => i.value === ip)) {
      notification.error("IP already blocked");
      return;
    }
    savePolicy(
      patch({
        ipBlocklist: [...list, { value: ip, label: (label || "").trim() || "Blocked IP" }],
        ipAllowlist: (policy.ipAllowlist || []).filter((i) => i.value !== ip),
      }),
    );
    setIpValue("");
    setIpLabel("");
  };

  const removeBlockedIp = (value) => {
    savePolicy(
      patch({
        ipBlocklist: (policy.ipBlocklist || []).filter((i) => i.value !== value),
      }),
    );
  };

  // GEO ACCESS: other role IP lists stay in company admin Geographic Access.

  if (!policy) {
    return <div className="p-6 text-sm text-gray-500">Loading geographic access...</div>;
  }

  const tabs = [
    // { id: "employees", label: t("GEO_TAB_EMPLOYEES") || "Employee locations" },
    { id: "countries", label: t("GEO_TAB_COUNTRIES") },
    { id: "states", label: t("GEO_TAB_STATES") },
    // { id: "ips", label: t("GEO_TAB_IPS") },
    { id: "logs", label: t("GEO_TAB_LOGS") },
  ];

  return (
    <div className="bg-[#F9F9F9]">
      <div className="px-3 py-4">
        <div className="bg-white border border-[#E9EDF9] rounded-lg">
          <div className="px-4 py-4 border-b border-[#E3E3E3] flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">{t("GEO_ACCESS")}</h2>
              <p className="text-sm text-gray-500">{t("GEO_ACCESS_HELP")}</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!policy.enabled}
                onChange={(e) => savePolicy(patch({ enabled: e.target.checked }))}
              />
              {t("GEO_ENABLE")}
            </label>
          </div>

          {/* GEO ACCESS: IP allowlist now lives in company admin, not Super Admin.
          {policy.enabled && !policy.ipAllowlistOnly && (
            <div className="mx-4 mt-3 p-3 rounded-lg bg-amber-50 text-amber-900 text-sm">
              Geographic Access is ON, but only allow location is still OFF...
            </div>
          )}
          */}

          <div className="px-4 pt-3 flex flex-wrap gap-2">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`px-4 py-2 text-sm rounded-lg border ${tab === item.id
                    ? "bg-LightBlue text-white border-transparent"
                    : "bg-white text-gray-700 border-[#E3E3E3]"
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tab === "countries" && (
            <div className="p-4">
              <p className="text-sm text-gray-500 mb-3">
                Allow India = only India can login. High-risk = that country can login with MFA, and is not also on Allowed. Block = no login. Super Admin is never locked.
              </p>
              {/* <p className="text-sm text-gray-500 mb-3">
                Open the country list and tick one or more countries, then Allow selected or
                Block selected. Blocked countries cannot login even if their IP is on an allow list.
              </p> */}
              <div className="flex flex-wrap gap-2 mb-4 items-start">
                <MultiCheckSelect
                  options={countries}
                  value={countryPicks}
                  onChange={setCountryPicks}
                  placeholder="Select countries"
                  getValue={(c) => String(c._id || c.id || "")}
                  getLabel={(c) => `${c.name} (${c.countryCode})`}
                />
                <button
                  type="button"
                  className="bg-LightBlue text-white px-4 py-2 rounded-lg"
                  onClick={() => addCountries("allowedCountries", countryPicks)}
                >
                  Allow selected
                </button>
                <button
                  type="button"
                  className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
                  onClick={() => addCountries("blockedCountries", countryPicks)}
                >
                  Block selected
                </button>
                <button
                  type="button"
                  className="border px-4 py-2 rounded-lg"
                  onClick={() => addCountries("highRiskCountries", countryPicks)}
                >
                  Mark high-risk
                </button>
              </div>
              <CountryTable
                title="Allowed countries"
                rows={policy.allowedCountries}
                onRemove={(row) => askRemoveCountry("allowedCountries", row)}
              />
              <CountryTable
                title="Blocked countries"
                rows={policy.blockedCountries}
                onRemove={(row) => askRemoveCountry("blockedCountries", row)}
              />
              <CountryTable
                title="High-risk countries"
                rows={policy.highRiskCountries}
                onRemove={(row) => askRemoveCountry("highRiskCountries", row)}
              />
            </div>
          )}

          {tab === "states" && (
            <div className="p-4">
              <p className="text-sm text-gray-500 mb-3">
                Pick a country, then Allow or Block selected states. A state can be on only one
                list. Blocked states cannot login.
              </p>
              <div className="flex flex-wrap gap-2 mb-4 items-start">
                <select
                  className="border rounded-lg px-3 py-2 min-w-[200px]"
                  value={selectedCountryId}
                  onChange={(e) => {
                    setSelectedCountryId(e.target.value);
                    setStatePicks([]);
                    setStatePick("");
                  }}
                >
                  <option value="">Select country</option>
                  {countries.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <MultiCheckSelect
                  options={Array.isArray(states) ? states : []}
                  value={statePicks}
                  onChange={setStatePicks}
                  placeholder="Select states"
                  getValue={(s) => String(s._id || s.id || "")}
                  getLabel={(s) => `${s.name} (${s.code || s.abbreviation || ""})`}
                />
                <button
                  type="button"
                  className="bg-LightBlue text-white px-4 py-2 rounded-lg"
                  onClick={() => addStates("allowedStates", statePicks)}
                >
                  Allow selected
                </button>
                <button
                  type="button"
                  className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
                  onClick={() => addStates("blockedStates", statePicks)}
                >
                  Block selected
                </button>
              </div>
              <StateTable
                title="Allowed states"
                rows={policy.allowedStates}
                onRemove={(row) =>
                  askRemove(
                    row.name || row.stateCode,
                    () =>
                      savePolicy(
                        patch({
                          allowedStates: (policy.allowedStates || []).filter(
                            (s) =>
                              !(s.countryCode === row.countryCode && s.stateCode === row.stateCode),
                          ),
                        }),
                      ),
                    STATE_LIST_LABEL.allowedStates,
                  )
                }
              />
              <StateTable
                title="Blocked states"
                rows={policy.blockedStates}
                onRemove={(row) =>
                  askRemove(
                    row.name || row.stateCode,
                    () =>
                      savePolicy(
                        patch({
                          blockedStates: (policy.blockedStates || []).filter(
                            (s) =>
                              !(s.countryCode === row.countryCode && s.stateCode === row.stateCode),
                          ),
                        }),
                      ),
                    STATE_LIST_LABEL.blockedStates,
                  )
                }
              />
            </div>
          )}

          {/* {tab === "ips" && (
            <div className="p-4">
              <h3 className="font-medium mb-1">{t("GEO_SECTION_COMPANY_ADMIN")}</h3>
              <p className="text-sm text-gray-500 mb-3">{t("GEO_SECTION_COMPANY_ADMIN_HELP")}</p>
              {policy.currentIp && (
                <p className="text-sm mb-3">
                  This computer: <strong>{policy.currentIp}</strong>
                </p>
              )}
              <div className="flex flex-wrap gap-2 mb-3">
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("GEO_IP_PLACEHOLDER")}
                  value={ipValue}
                  onChange={(e) => setIpValue(e.target.value)}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("GEO_LABEL")}
                  value={ipLabel}
                  onChange={(e) => setIpLabel(e.target.value)}
                />
                <button
                  type="button"
                  className="bg-LightBlue text-white px-4 py-2 rounded-lg"
                  onClick={() => addCompanyAdminIp(ipValue, ipLabel)}
                >
                  {t("GEO_ADD_IP")}
                </button>
                <button
                  type="button"
                  className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
                  onClick={() => addBlockedIp(ipValue, ipLabel)}
                >
                  {t("GEO_BLOCK_IP")}
                </button>
                {policy.currentIp && (
                  <button
                    type="button"
                    className="border px-4 py-2 rounded-lg"
                    onClick={() => addCompanyAdminIp(policy.currentIp, t("GEO_THIS_COMPUTER"))}
                  >
                    {t("GEO_ALLOW_MY_IP")}
                  </button>
                )}
              </div>
              <div className="border rounded-lg overflow-hidden">
                {companyAdminIps.length === 0 ? (
                  <div className="p-3 text-sm text-gray-400">{t("GEO_NONE")}</div>
                ) : (
                  companyAdminIps.map((row) => (
                    <div
                      key={`${row.role || "any"}-${row.value}`}
                      className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 text-sm"
                    >
                      <span>
                        {row.value} {row.label ? `— ${row.label}` : ""}
                      </span>
                      <button
                        type="button"
                        className="text-red-500"
                        onClick={() =>
                          askRemove(row.value, () => removeCompanyAdminIp(row.value), "Allowed")
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
              <h3 className="font-medium mt-8 mb-1">Blocked IPs</h3>
              <p className="text-sm text-gray-500 mb-3">
                Super Admin can block any IP (company admin, HR, accountant, or employee).
                A blocked IP cannot login even if it is on an allow list. Super Admin is never locked.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
                  onClick={() => addBlockedIp(ipValue, ipLabel)}
                >
                  Block IP
                </button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                {(policy.ipBlocklist || []).length === 0 ? (
                  <div className="p-3 text-sm text-gray-400">{t("GEO_NONE")}</div>
                ) : (
                  (policy.ipBlocklist || []).map((row) => (
                    <div
                      key={`block-${row.value}`}
                      className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 text-sm"
                    >
                      <span>
                        {row.value} {row.label ? `— ${row.label}` : ""}
                      </span>
                      <button
                        type="button"
                        className="text-red-500"
                        onClick={() =>
                          askRemove(row.value, () => removeBlockedIp(row.value), "Blocked")
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )} */}

          {/* {tab === "employees" && (
            <div className="p-4 grid lg:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-3">
                  Location employees entered in their profile (saved with Google Maps in the employee app).
                  Open a row to check that pin exists on the map. This is not your IP location.
                </p>
                <div className="flex gap-2 mb-3">
                  <input
                    className="border rounded-lg px-3 py-2 flex-1"
                    placeholder="Search name, email, address"
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="bg-LightBlue text-white px-4 py-2 rounded-lg"
                    onClick={() => loadEmployeeLocations(1, empSearch)}
                  >
                    Search
                  </button>
                </div>
                <div className="border rounded-lg overflow-hidden text-sm">
                  {employees.length === 0 ? (
                    <div className="p-3 text-gray-400">No employee profile locations yet</div>
                  ) : (
                    employees.map((emp) => {
                      const hasCoords =
                        Number.isFinite(Number(emp.latitude)) &&
                        Number.isFinite(Number(emp.longitude));
                      return (
                        <button
                          type="button"
                          key={emp._id}
                          onClick={() => setSelectedEmployee(emp)}
                          className={`w-full text-left px-3 py-2 border-b last:border-b-0 ${
                            selectedEmployee?._id === emp._id ? "bg-[#F3F6FF]" : "bg-white"
                          }`}
                        >
                          <div className="font-medium">{emp.name || emp.email}</div>
                          <div className="text-gray-500">
                            {emp.companyId?.name ? `${emp.companyId.name} · ` : ""}
                            {[emp.addressLine1, emp.city].filter(Boolean).join(", ") || "No address"}
                          </div>
                          <div className={hasCoords ? "text-green-600" : "text-amber-600"}>
                            {hasCoords
                              ? emp.addressVerified
                                ? "Location saved — exists on map"
                                : "Coordinates saved — check map"
                              : "No map coordinates — cannot verify"}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    className="border px-3 py-1 rounded"
                    disabled={empPage <= 1}
                    onClick={() => loadEmployeeLocations(empPage - 1)}
                  >
                    Previous
                  </button>
                  <span className="text-sm py-1">
                    Page {empPage} / {empTotalPages}
                  </span>
                  <button
                    type="button"
                    className="border px-3 py-1 rounded"
                    disabled={empPage >= empTotalPages}
                    onClick={() => loadEmployeeLocations(empPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
              <div>
                {selectedEmployee &&
                Number.isFinite(Number(selectedEmployee.latitude)) &&
                Number.isFinite(Number(selectedEmployee.longitude)) ? (
                  <>
                    <p className="text-sm mb-2">
                      {selectedEmployee.name} — {selectedEmployee.addressLine1 || ""}{" "}
                      {selectedEmployee.city || ""}
                    </p>
                    <LocationMap
                      key={selectedEmployee._id}
                      latitude={Number(selectedEmployee.latitude)}
                      longitude={Number(selectedEmployee.longitude)}
                      height="320px"
                      zoom={15}
                    />
                  </>
                ) : (
                  <div className="h-[320px] rounded-lg border border-[#E9EDF9] flex items-center justify-center text-sm text-gray-500 px-4 text-center">
                    Select an employee with saved coordinates to verify their profile location on the map.
                  </div>
                )}
              </div>
            </div>
          )} */}

          {tab === "logs" && (
            <div className="p-4">
              <div className="overflow-x-auto relative rounded-lg border border-[#E1E6EE]">
                <table className="min-w-[980px] w-full text-xs text-left text-[#6c6c6c] border-collapse">
                  <thead className="text-xs text-gray-900 bg-[#E1E6EE]">
                    <tr>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">
                        Time
                      </th>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">
                        Email
                      </th>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">
                        Role
                      </th>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">
                        IP
                      </th>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">
                        Location
                      </th>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">
                        Decision
                      </th>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">
                        Reasons
                      </th>
                      <th scope="col" className="py-3 px-4 whitespace-nowrap">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-6 px-4 text-center text-gray-400">
                          No geo logs yet
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => {
                        // PREVIOUS: [loginAddress, city, regionName, countryName].join
                        // mixed GPS Noida with IP Gurgaon / Local network.
                        const locationText = formatGeoLogLocation(log);
                        return (
                          <tr
                            key={log._id}
                            className="bg-white border-b border-[#E9EDF9] last:border-b-0 align-top"
                          >
                            <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9] text-gray-900">
                              {dayjs(log.createdAt).format("MM-DD-YYYY")}
                              <div className="text-[#8A8A8A]">
                                {dayjs(log.createdAt).format("h:mm A")}
                              </div>
                            </td>
                            <td className="py-3 px-4 border-r border-[#E9EDF9] text-gray-900 break-all min-w-[180px]">
                              {log.email || "-"}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9]">
                              {log.actorRole || "-"}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9] font-mono">
                              {log.ip || "-"}
                            </td>
                            <td className="py-3 px-4 border-r border-[#E9EDF9] min-w-[220px] max-w-[320px] whitespace-normal break-words">
                              {locationText}
                              {log.isVpnOrProxy ? (
                                <span className="ml-1 text-amber-700">· VPN</span>
                              ) : null}
                              {log.isTor ? (
                                <span className="ml-1 text-amber-700">· Tor</span>
                              ) : null}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9]">
                              <span
                                className={`inline-block px-2 py-1 rounded font-medium ${log.decision === "DENY"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                  }`}
                              >
                                {log.decision || "-"}
                              </span>
                            </td>
                            <td className="py-3 px-4 min-w-[180px] whitespace-normal break-words border-r border-[#E9EDF9]">
                              {(log.reasons || []).length
                                ? (log.reasons || []).join(", ")
                                : "-"}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <button
                                type="button"
                                className="text-red-500"
                                onClick={() => deleteLog(log._id || log.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  className="border px-3 py-1 rounded"
                  disabled={logPage <= 1}
                  onClick={() => loadLogs(logPage - 1)}
                >
                  Previous
                </button>
                <span className="text-sm py-1">
                  Page {logPage} / {logTotalPages}
                </span>
                <button
                  type="button"
                  className="border px-3 py-1 rounded"
                  disabled={logPage >= logTotalPages}
                  onClick={() => loadLogs(logPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {saving && (
            <div className="px-4 pb-4 text-xs text-gray-400">Saving...</div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={!!listConfirm}
        onClose={() => setListConfirm(null)}
        onConfirm={() => {
          listConfirm?.run?.();
          setListConfirm(null);
        }}
        isDanger
        title={listConfirm?.title || "Confirm?"}
        message={listConfirm?.message || "Do you still want to continue?"}
        confirmText={listConfirm?.confirmText || "Confirm"}
        cancelText="Cancel"
      />
    </div>
  );
};

const CountryTable = ({ title, rows, onRemove }) => (
  <div className="mb-6">
    <h3 className="font-medium mb-2">{title}</h3>
    <div className="border rounded-lg overflow-hidden">
      {(rows || []).length === 0 ? (
        <div className="p-3 text-sm text-gray-400">None</div>
      ) : (
        (rows || []).map((row, idx) => (
          <div
            key={`${row.countryId || row.code || row.name}-${idx}`}
            className="flex justify-between items-center px-3 py-2 border-b last:border-b-0"
          >
            <span>
              {row.name} ({row.code})
            </span>
            <button type="button" className="text-red-500 text-sm" onClick={() => onRemove(row)}>
              Remove
            </button>
          </div>
        ))
      )}
    </div>
  </div>
);

const StateTable = ({ title, rows, onRemove }) => (
  <div className="mb-6">
    <h3 className="font-medium mb-2">{title}</h3>
    <div className="border rounded-lg overflow-hidden">
      {(rows || []).length === 0 ? (
        <div className="p-3 text-sm text-gray-400">None</div>
      ) : (
        (rows || []).map((row) => (
          <div
            key={`${row.countryCode}-${row.stateCode}`}
            className="flex justify-between items-center px-3 py-2 border-b last:border-b-0"
          >
            <span>
              {row.stateName} ({row.stateCode}), {row.countryName}
            </span>
            <button type="button" className="text-red-500 text-sm" onClick={() => onRemove(row)}>
              Remove
            </button>
          </div>
        ))
      )}
    </div>
  </div>
);

const IpTable = ({ title, rows, onRemove, onAllow }) => (
  <div className="mb-6">
    <h3 className="font-medium mb-2">{title}</h3>
    <div className="border rounded-lg overflow-hidden">
      {(rows || []).length === 0 ? (
        <div className="p-3 text-sm text-gray-400">None</div>
      ) : (
        (rows || []).map((row) => (
          <div
            key={row.value}
            className="flex justify-between items-center px-3 py-2 border-b last:border-b-0"
          >
            <span>
              {row.value} {row.label ? `— ${row.label}` : ""}
            </span>
            <div className="flex gap-3">
              {onAllow && (
                <button
                  type="button"
                  className="text-blue-600 text-sm"
                  onClick={() => onAllow(row)}
                >
                  Allow
                </button>
              )}
              <button
                type="button"
                className="text-red-500 text-sm"
                onClick={() => onRemove(row.value)}
              >
                Remove
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const MultiCheckSelect = ({ options, value, onChange, placeholder, getValue, getLabel }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const picked = (value || []).map((v) => String(v));

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const optionId = (o) => String(getValue(o) || "");
  const selected = (options || []).filter((o) => picked.includes(optionId(o)));
  const closedLabel = selected.length
    ? `${selected.length} selected`
    : placeholder;
  const filtered = (options || []).filter((o) =>
    String(getLabel(o) || "")
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  const visibleIds = filtered.map(optionId).filter(Boolean);
  const allVisiblePicked =
    visibleIds.length > 0 && visibleIds.every((id) => picked.includes(id));

  const toggle = (id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const sid = String(id);
    if (!sid) return;
    onChange(
      picked.includes(sid)
        ? picked.filter((v) => v !== sid)
        : [...picked, sid],
    );
  };

  const toggleAllVisible = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (allVisiblePicked) {
      onChange(picked.filter((id) => !visibleIds.includes(id)));
    } else {
      onChange([...new Set([...picked, ...visibleIds])]);
    }
  };

  return (
    <div className="relative min-w-[280px]" ref={ref}>
      <button
        type="button"
        className="border rounded-lg px-3 py-2 w-full text-left bg-white flex items-center justify-between"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={selected.length ? "text-gray-800" : "text-gray-400"}>
          {closedLabel}
        </span>
        {/* <span className="text-gray-400 text-xs ml-2">tick multiple</span> */}
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 w-full overflow-hidden border rounded-lg bg-white shadow"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            className="w-full border-b px-3 py-2 text-sm"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-xs text-LightBlue border-b"
            onClick={toggleAllVisible}
          >
            {allVisiblePicked ? "Clear visible" : "Select all visible"}
          </button>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">None</div>
            ) : (
              filtered.map((o, index) => {
                const id = optionId(o);
                return (
                  <label
                    key={id || `opt-${index}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm cursor-pointer"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => toggle(id, e)}
                  >
                    <input
                      type="checkbox"
                      checked={picked.includes(id)}
                      readOnly
                    />
                    {getLabel(o)}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeoAccess;
